import { Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import TwoFactorSetupModal from '../../componentsv2/TwoFactoSetupModal';
import { useAppDispatch, setShowTfaSetupModal } from '../../store';
import { useAuth } from '../../hooks';
import { BANKING, DASHBOARD, LENDER_DASHBOARD, TWO_FACTOR_AUTHENTICATION } from '../../constants/auth';
import { getRedirectPage, getUrlParams } from '../../utils/helpers';
import { logUrlParamsEvents } from '../../utils/tracking';

type AuthGuardProps = {
  isPublicRoute?: boolean;
  children: React.ReactNode;
  isAuth?: boolean;
  isAdmin?: boolean;
  isLender?: boolean;
  isCompanyUserRoute?: boolean;
  isAdminRoute?: boolean;
  isLenderRoute?: boolean;
  isAllAccessible?: boolean;
  zeroCompanies?: boolean;
  path?: string;
};

const AuthGuard = (props: AuthGuardProps) => {
  const { showTfaSetupModal, showTfaPage, user, tfaValidated } = useAuth();
  const dispatch = useAppDispatch();
  const {
    children,
    isPublicRoute = false,
    isAuth = false,
    isCompanyUserRoute = false,
    isAdminRoute = false,
    isLenderRoute = false,
    isAdmin,
    isLender = false,
    isAllAccessible = false,
    path,
  } = props;

  const params = getUrlParams();
  // Only log url params events the first time
  useEffect(() => {
    logUrlParamsEvents(params);
  }, []);

  // Banking requires 2FA, and this user has not set it up. The redirect below is what
  // keeps them out, but the setup modal is only ever opened by a request that fails with
  // two_factor_authentication_setup_required, and the redirect happens before any
  // request is made. So someone who dismissed the modal once was bounced back to the
  // dashboard with no explanation, every time, with no way to reach the setup again.
  const isBankingPath = path === BANKING || !!path?.startsWith(`${BANKING}/`);
  const needsTfaSetupForBanking = Boolean(isAuth && user?.tfaRequired && !user?.tfaEnabled && isBankingPath);

  useEffect(() => {
    if (needsTfaSetupForBanking) {
      dispatch(setShowTfaSetupModal(true));
    }
  }, [needsTfaSetupForBanking, dispatch]);

  // If the user is on the 2FA page, don't redirect
  if (path === TWO_FACTOR_AUTHENTICATION) {
    return <>{children}</>;
  }

  // If the user already set up 2FA (showTfaPage is only set at login when tfaEnabled) but
  // hasn't validated this session, redirect to the 2FA code-entry page.
  if (isAuth && user?.tfaRequired && !tfaValidated && showTfaPage) {
    const redirectPage = getRedirectPage(params);
    return (
      <Navigate
        to={`${TWO_FACTOR_AUTHENTICATION}${redirectPage ? `?next=${redirectPage}` : '?next=dashboard'}`}
        replace
      />
    );
  }

  // If it's a public route and the user is already authenticated, redirect to dashboard or specific route based on url params
  if (isAuth && isPublicRoute) {
    const redirectPage = getRedirectPage(params);
    if (redirectPage) return <Navigate to={redirectPage} replace />;
    return <Navigate to={isLender ? LENDER_DASHBOARD : DASHBOARD} replace />;
  }

  // If it's a private route and the user is not authenticated, redirect to the login page with a next parameter
  if (!isAuth && !isPublicRoute && !isAllAccessible) {
    // add `next` url Param (keeping preexisting ones if needed)
    const newParams = new URLSearchParams();
    const urlParams = getUrlParams();
    Object.entries(urlParams).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    newParams.set('next', window.location.pathname);

    return <Navigate to={`/login?${newParams.toString()}`} replace />;
  }

  // If it's a client route and the user is an admin or viceversa, redirect home
  if (
    (isAuth && isCompanyUserRoute && (isAdmin || isLender)) ||
    (isAdminRoute && !isAdmin) ||
    (isLenderRoute && !isLender)
  )
    return <Navigate to={isLender ? LENDER_DASHBOARD : '/'} replace />;

  // Sent to the dashboard, where the setup modal opened above is rendered
  if (needsTfaSetupForBanking) {
    return <Navigate to={DASHBOARD} replace />;
  }

  // If the user is authenticated, has tfa required, has tfaEnabled and the 2FA is not validated, redirect to the dashboard
  if (isAuth && user?.tfaRequired && user?.tfaEnabled && !tfaValidated && isBankingPath) {
    return <Navigate to={`${TWO_FACTOR_AUTHENTICATION}?next=${path}`} replace />;
  }

  return (
    <>
      {children}
      <TwoFactorSetupModal
        isOpen={showTfaSetupModal}
        handleClose={() => {
          dispatch(setShowTfaSetupModal(false));
        }}
        onSuccess={() => {
          dispatch(setShowTfaSetupModal(false));
        }}
      />
    </>
  );
};

export default AuthGuard;
